import { useState } from 'react';
import { useNavigate } from 'react-router';

import { setChosenDraft } from '@/shared/quotes/basket';
import { useCan } from '@/shared/session/profiles';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PlusIcon } from '@/shared/ui/icons';

import type { QuoteCreate } from './types';
import { useCreateQuote } from './useQuotes';

/**
 * «Nuevo presupuesto». Whoever makes them both ways (contract v10.52) is asked which: from
 * the tariffs, or from a supplier's offer that did not come by email. Whoever only makes
 * them from the tariffs (the shop) gets one straight away. A draft from the tariffs
 * becomes the one «Añadir» of Documentos fills, so the search and the editor speak of the
 * same draft.
 */
export function NewQuoteButton() {
  const navigate = useNavigate();
  const create = useCreateQuote();
  const offers = useCan('quotes');
  const tariffs = useCan('tariff_quotes');
  const [asking, setAsking] = useState(false);

  function make(payload: QuoteCreate): void {
    create.mutate(payload, {
      onSuccess: (quote) => {
        if (quote.origin === 'tariff') setChosenDraft(quote.id);
        setAsking(false);
        void navigate(`/presupuestos/${quote.id}`);
      },
    });
  }

  function start(): void {
    if (offers && tariffs) setAsking(true);
    else make(tariffs ? { origin: 'tariff' } : {});
  }

  return (
    <div className="flex flex-col items-end gap-2">
      <Button icon={<PlusIcon size={18} />} onClick={start} disabled={create.isPending}>
        {create.isPending && !asking ? 'Creando…' : 'Nuevo presupuesto'}
      </Button>
      {create.isError && !asking ? (
        <ErrorMessage error={create.error}>No se pudo crear el presupuesto</ErrorMessage>
      ) : null}
      {asking ? (
        <Dialog
          title="Nuevo presupuesto"
          size="sm"
          onClose={() => {
            setAsking(false);
            create.reset();
          }}
          busy={create.isPending}
        >
          <div className="flex flex-col gap-2">
            <ChoiceButton
              title="Desde las tarifas"
              hint="Busca los artículos en Documentos o pega la lista del cliente."
              onClick={() => make({ origin: 'tariff' })}
              disabled={create.isPending}
              autoFocus
            />
            <ChoiceButton
              title="De una oferta de proveedor"
              hint="Sube el documento que no llegó por correo, o teclea las líneas."
              onClick={() => make({ origin: 'offer' })}
              disabled={create.isPending}
            />
            {create.isPending ? (
              <p role="status" className="text-ink-3 text-[13px]">
                Creando…
              </p>
            ) : null}
            {create.isError ? (
              <ErrorMessage error={create.error}>No se pudo crear el presupuesto</ErrorMessage>
            ) : null}
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}

function ChoiceButton({
  title,
  hint,
  onClick,
  disabled,
  autoFocus = false,
}: {
  title: string;
  hint: string;
  onClick: () => void;
  disabled: boolean;
  autoFocus?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-autofocus={autoFocus ? '' : undefined}
      className="border-line hover:border-accent hover:bg-paper-2 focus-visible:outline-accent flex flex-col items-start gap-0.5 rounded-lg border px-4 py-3 text-left disabled:opacity-60"
    >
      <span className="text-ink text-[15px] font-semibold">{title}</span>
      <span className="text-ink-3 text-[13px] leading-4">{hint}</span>
    </button>
  );
}
